import type {
  ChannelCapability,
  ConnectionInput,
  DraftView,
  EmailSuggestions,
  OtpMode,
} from "../../../api/connectionHub.types";
import {
  CREDENTIAL_FIELD,
  INBOX_OWNER,
  OTP_MODE,
} from "../../../api/connectionHub.types";
import {
  CONNECTION_ERROR_CODES,
  getConnectionErrorDefinition,
} from "../../../constants/connectionErrorCodes";
import type {
  CredentialFormChannel,
  CredentialFormValues,
  EditingChannel,
} from "./CredentialsStep.types";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normalizeEmail = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();

export const isValidEmail = (value?: string | null): boolean =>
  EMAIL_PATTERN.test(normalizeEmail(value));

export const requiresInbox = (
  capability: ChannelCapability,
  otpMode?: OtpMode
): boolean =>
  capability.needsInbox &&
  (otpMode ?? capability.defaultOtpMode) === OTP_MODE.EMAIL;

export const initialCredentialValue = (
  ipId: string,
  capability: ChannelCapability,
  draft?: DraftView,
  // The connection being edited wins over any stale draft: its email is the one the card is locked to.
  editing?: EditingChannel
): CredentialFormChannel => ({
  ipId,
  email: editing?.email ?? draft?.loginEmail ?? "",
  password: "",
  passwordEdited: false,
  hasStoredPassword: editing?.hasPassword ?? draft?.hasPassword ?? false,
  otpMode:
    editing?.otpMode ??
    draft?.otpMode ??
    (capability.otpModes.length === 1
      ? capability.otpModes[0]
      : capability.defaultOtpMode ?? undefined),
  inboxOwner: editing?.inboxOwner ?? draft?.inboxOwner ?? undefined,
});

export const toConnectionInput = (
  value: CredentialFormChannel,
  capability: ChannelCapability
): ConnectionInput => {
  const input: ConnectionInput = { ipId: value.ipId };
  if (capability.fields.includes(CREDENTIAL_FIELD.EMAIL)) input.email = value.email;
  if (capability.fields.includes(CREDENTIAL_FIELD.PASSWORD) && value.passwordEdited) {
    input.password = value.password;
  }
  if (value.otpMode) input.otpMode = value.otpMode;
  if (requiresInbox(capability, value.otpMode) && value.inboxOwner) {
    input.inboxOwner = value.inboxOwner;
  }
  return input;
};

const message = (code: string): string =>
  getConnectionErrorDefinition(code)?.message ?? "Invalid value";

export type CredentialChannelErrors = Partial<
  Record<keyof CredentialFormChannel, string>
>;

export interface CredentialFormErrors {
  channels?: Array<CredentialChannelErrors | undefined>;
}

export const validateCredentials = (
  values: CredentialFormValues,
  selectedIpIds: string[],
  capabilities: Map<string, ChannelCapability>
): CredentialFormErrors => {
  const selected = new Set(selectedIpIds);
  const errors: Array<CredentialChannelErrors | undefined> = [];

  values.channels.forEach((value, index) => {
    if (!selected.has(value.ipId)) return;
    const capability = capabilities.get(value.ipId);
    if (!capability) return;
    const channelErrors: CredentialChannelErrors = {};

    if (capability.fields.includes(CREDENTIAL_FIELD.EMAIL)) {
      if (!normalizeEmail(value.email)) {
        channelErrors.email = message(CONNECTION_ERROR_CODES.EMAIL_REQUIRED);
      } else if (!isValidEmail(value.email)) {
        channelErrors.email = message(CONNECTION_ERROR_CODES.INVALID_EMAIL);
      }
    }
    if (
      capability.fields.includes(CREDENTIAL_FIELD.PASSWORD) &&
      (value.passwordEdited ? !value.password : !value.hasStoredPassword)
    ) {
      channelErrors.password = message(CONNECTION_ERROR_CODES.PASSWORD_REQUIRED);
    }
    if (capability.otpModes.length > 1 && !value.otpMode) {
      channelErrors.otpMode = message(CONNECTION_ERROR_CODES.OTP_MODE_REQUIRED);
    }
    if (requiresInbox(capability, value.otpMode) && !value.inboxOwner) {
      channelErrors.inboxOwner = message(
        CONNECTION_ERROR_CODES.INBOX_OWNER_REQUIRED
      );
    }
    if (Object.keys(channelErrors).length) errors[index] = channelErrors;
  });

  return errors.some(Boolean) ? { channels: errors } : {};
};

export const mergeEmailSuggestions = (
  suggestions: EmailSuggestions | null,
  values: CredentialFormChannel[]
): string[] => {
  const merged = [
    ...(suggestions?.emails ?? []),
    ...values.map((value) => value.email),
  ];
  const seen = new Set<string>();
  return merged.filter((email) => {
    const normalized = normalizeEmail(email);
    if (!isValidEmail(normalized) || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};
